import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../../core/tools/interfaces/ITool';
import { ICommand } from '../../../core/commands/ICommand';
import { UpdateEdgeCommand } from '../../../core/commands/edges/UpdateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import { Badge } from '../../../components/ui/badge';
import { cn } from '@/lib/utils';
import { RelationshipSubtypeSwitcher } from '../../../components/ui/RelationshipSubtypeSwitcher';
import { usePerspectiveStore } from '../../../stores/perspectiveStore';
import { UserStar, Home, Baby, Zap } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n/config';
import { findSubtypeForEdge } from '../../../lib/diagram/registries';
import { bindGenogramToolName } from './utils/toolNameI18n';

// Flag labels for display - now handled by translation
// Keeping this for fallback purposes
const FLAG_LABELS: Record<string, string> = {
  divorced: 'Divorced',
  separated: 'Separated',
  engaged: 'Engaged',
  house: 'Cohabitation',
  solidDashedDouble: 'Committed Relationship',
  cohabSeparation: 'Cohabitation and Separation',
  nonSentimentalCohabSep: 'Non-sentimental Cohabitation and Separation',
  nonSentimentalCohab: 'Non-sentimental Cohabitation',
  temporaryRelation: 'Temporary relation / One night stand',
  legalCohabLegalSep: 'Legal cohabitation and official (legal) separation',
  half: 'Half',
  step: 'Step',
  adopted: 'Adopted',
  guardianship: 'Guardianship',
  dotted: 'Dotted',
  dashed: 'Dashed',
};

// Map flags to their corresponding SVG icons
const FLAG_TO_ICON: Record<string, string> = {
  divorced: '/assets/relationships/genogram/divorce.svg',
  separated: '/assets/relationships/genogram/separation.svg',
  engaged: '/assets/relationships/genogram/engagement.svg',
  house: '/assets/relationships/genogram/cohabitation.svg',
  solidDashedDouble: '/assets/relationships/genogram/committed_relationship.svg',
  cohabSeparation: '/assets/relationships/genogram/cohabitation_separation.svg',
  nonSentimentalCohabSep: '/assets/relationships/genogram/non_sentimental_cohab_separation.svg',
  nonSentimentalCohab: '/assets/relationships/genogram/non_sentimental_cohabitation.svg',
  temporaryRelation: '/assets/relationships/genogram/temporary_relation.svg',
  legalCohabLegalSep: '/assets/relationships/genogram/legal_cohab_legal_separation.svg',
  half: '/assets/relationships/genogram/half_sibling.svg',
  step: '/assets/relationships/genogram/step_child.svg',
  adopted: '/assets/relationships/genogram/adoptive_child.svg',
  guardianship: '/assets/relationships/genogram/foster_child.svg',
  dashed: '/assets/relationships/genogram/dashed.svg',
  dotted: '/assets/relationships/genogram/dotted.svg',
};

// Organized flag groups for better UX - labels now translated
const FLAG_GROUPS = {
  status: {
    labelKey: 'status',
    icon: UserStar, // Relationship status (divorced, separated, engaged)
    flags: ['divorced', 'separated', 'engaged'] as const
  },
  living: {
    labelKey: 'living',
    icon: Home, // Living situation (house/cohabitation, committed relationship, cohabitation separation)
    flags: ['house', 'solidDashedDouble', 'cohabSeparation', 'nonSentimentalCohabSep', 'nonSentimentalCohab', 'temporaryRelation', 'legalCohabLegalSep'] as const
  },
  relationship: {
    labelKey: 'relationship',
    icon: Baby, // Relationship type (half, step, adopted, guardianship)
    flags: ['half', 'step', 'adopted', 'guardianship'] as const
  },
  style: {
    labelKey: 'style',
    icon: Zap, // Visual style (dotted, dashed)
    flags: ['dotted', 'dashed'] as const
  }
} as const;

type BoolFlag = (typeof FLAG_GROUPS)[keyof typeof FLAG_GROUPS]['flags'][number];

const RelationshipEditPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const selection = Array.from(context.selection);
  const edges = useDiagramStore(s => s.edges);
  const selectedEdges = edges.filter(e => selection.includes(e.id));

  if (selectedEdges.length === 0) {
    return (
      <div className="text-center text-muted-foreground p-4 border rounded-md text-sm">
        {t('panels.selectRelationshipToEdit', { defaultValue: 'Select a relationship to edit' })}
      </div>
    );
  }

  // const first = selectedEdges[0] as any;
  const diagramType = context.diagramType as 'genogram';
  // categories are provided by switcher; no local usage
  const showCaptions = usePerspectiveStore.getState().showRelationshipCaptions;

  const defaultLabelFor = (edgeType: string, attrs: Record<string, unknown> = {}): string => {
    const sub = findSubtypeForEdge(diagramType, edgeType, attrs);
    if (sub) {
      const key = sub.label;
      if (key.startsWith('relationships.')) return i18n.t(`diagram:${key}`, key);
      return key;
    }
    return 'Connection';
  };

  const mixed = (flag: BoolFlag): boolean | 'mixed' => {
    let v: boolean | undefined = undefined;
    for (const e of selectedEdges) {
      const val = Boolean((e as any).attrs?.[flag]);
      if (v === undefined) v = val; else if (v !== val) return 'mixed';
    }
    return v ?? false;
  };

  const toggleFlag = (flag: BoolFlag) => {
    const cmds: ICommand[] = [];
    const current = mixed(flag);
    const next = current === 'mixed' ? true : !current;
    for (const e of selectedEdges) {
      const attrs = { ...(e as any).attrs, [flag]: next } as any;
      const autoLabel = showCaptions && (
        attrs.label == null || attrs.label === 'Connection' || attrs.__autoLabel === true
      );
      if (autoLabel) {
        attrs.label = defaultLabelFor((e as any).type as string, attrs);
        attrs.__autoLabel = true;
      }
      cmds.push(new UpdateEdgeCommand(e.id, { attrs } as any));
    }
    return cmds;
  };

  // subtype switching handled by shared component

  // Color swatches removed per UX simplification


  return (
    <div className="flex flex-col min-w-0 w-full">
      {/* Relationship Type Selection */}
      <RelationshipSubtypeSwitcher
        diagramType={diagramType}
        selectedEdgeIds={selectedEdges.map(e => e.id)}
        onExecute={(cmds) => onExecute(cmds)}
      />

      {/* Relationship Attributes */}
      <div className="px-4 pt-3 pb-4 space-y-4">
        {Object.entries(FLAG_GROUPS).map(([groupKey, group]) => {
          return (
            <div key={groupKey}>
              <h3 className="text-xs font-semibold text-foreground mb-3">
                {t(`relationships.flagGroups.${group.labelKey}`, group.labelKey)}
              </h3>
              <div className="grid grid-cols-5 gap-1.5 w-full max-w-[398px]">
                {group.flags.map(flag => {
                  const state = mixed(flag);
                  const isMixed = state === 'mixed';
                  const isActive = state === true;
                  const flagLabel = t(`relationships.flags.${flag}`, FLAG_LABELS[flag] || flag);
                  const iconPath = FLAG_TO_ICON[flag];
                  
                  return (
                    <button
                      key={flag}
                      onClick={() => onExecute(toggleFlag(flag))}
                      data-testid={`relationship-flag-${flag}`}
                      className={cn(
                        'flex flex-col items-center justify-center gap-1.5 p-2.5 rounded-lg border transition-all',
                        'bg-background hover:bg-muted/50',
                        'h-[96px] w-full min-w-0',
                        isActive
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-border hover:border-foreground/20'
                      )}
                      title={isMixed ? `${flagLabel} (mixed values)` : flagLabel}
                    >
                      {iconPath && (
                        <img
                          src={iconPath}
                          alt={flagLabel}
                          className="h-5 w-auto flex-shrink-0"
                        />
                      )}
                      <span className="text-xs text-center text-muted-foreground leading-tight line-clamp-2">
                        {flagLabel}
                      </span>
                      {isMixed && (
                        <Badge variant="outline" className="text-[8px] px-1 py-0 mt-1">
                          mixed
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export class RelationshipEditTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-relationship-edit',
    name: i18n.t('genogram:tools.relationshipEditor', { defaultValue: 'Relationship Editor' }) as string,
    icon: 'link',
    category: 'edit',
    description: 'Switch relationship type and flags; quick style',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram'],
    requiresSelection: true,
    minSelection: 1,
    maxSelection: undefined,
    ui: { toolbar: false, contextMenu: false, propertyPanel: true, sidebar: false, floatingPanel: false, statusBar: false },
    applicability: {
      diagramTypes: ['genogram'],
      contextTypes: ['connector'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: true,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.relationshipEditor', 'Relationship Editor');
  }

  canExecute(context: IToolContext): boolean {
    // Only allow for genogram diagrams
    if (context.diagramType !== 'genogram') {
      return false;
    }
    
    // allow when at least one selected id is an edge id present in store
    // but exclude free lines (new Line shapes) - they don't need the relationship library panel
    // Allow existing connector lines (created from shape connection points)
    const ids = Array.from(context.selection);
    const edges = useDiagramStore.getState().edges;
    const nodes = useDiagramStore.getState().nodes;
    return ids.some(id => {
      const edge = edges.find(e => e.id === id);
      if (!edge) return false;

      const fromNode = nodes.find((n) => n.id === edge.from);
      const toNode = nodes.find((n) => n.id === edge.to);
      const hasImageEndpoint = fromNode?.type === 'image' || toNode?.type === 'image';
      if (hasImageEndpoint) {
        return false;
      }
      
      // Check if it's a free line (new Line shape from shape picker)
      const isFreeLine = edge.type === 'line' && 
        edge.from?.startsWith('__freeF:') && 
        edge.to?.startsWith('__freeT:') &&
        (edge.attrs as any)?.fromPoint &&
        (edge.attrs as any)?.toPoint;
      
      // Exclude free lines, but allow everything else (including existing connector lines)
      return !isFreeLine;
    });
  }

  execute(params?: any): ICommand | ICommand[] | null {
    if (!params) return null;
    if (Array.isArray(params)) return params;
    return null;
  }

  getPropertyPanelComponent() {
    return RelationshipEditPanel;
  }
}
